import React, { useContext, useRef, useState } from "react"
import { Dialog } from "@reach/dialog"
import "@reach/dialog/styles.css"
import {
  Elements,
  useStripe,
  useElements,
  CardElement,
} from "@stripe/react-stripe-js"
import { loadStripe, Stripe } from "@stripe/stripe-js"
import { UserContext } from "../../HomeApp"
import CardSection from "../../tips/CardSection"
import { SubmitButton } from "../../shared/Button"
import { formatPrice } from "../../../util/ticketUtils"
import {
  guestCheckout,
  updateGuestProfile,
} from "../../../requests/ticketPurchases"
import { X } from "react-bootstrap-icons"
import { TicketEventInfo } from "../TicketEventInfo"
import { BandInterestPrompt } from "../BandInterestPrompt"
import { useDispatch } from "react-redux"
import { openAuthDialog } from "../../Authentication"
import { checkEmailForIssues, EmailIssue } from "../../../util/emailSanityCheck"

const MAX_GUEST_TICKETS = 4

interface LoggedOutCheckoutModalProps {
  postId: number
  bandName: string
  eventTitle: string
  ticketPrice: number
  onClose: () => void
  concert: { [key: string]: any } | null
  maxTickets: number
  ticketsSold: number
}

interface GuestCheckoutFormProps {
  postId: number
  bandName: string
  eventTitle: string
  ticketPrice: number
  onClose: () => void
  concert: { [key: string]: any } | null
  maxTickets: number
  ticketsSold: number
}

const GuestCheckoutForm: React.FC<GuestCheckoutFormProps> = ({
  postId,
  bandName,
  eventTitle,
  ticketPrice,
  onClose,
  concert,
  maxTickets,
  ticketsSold,
}) => {
  const stripe = useStripe()
  const elements = useElements()
  const dispatch = useDispatch()
  const [email, setEmail] = useState("")
  const [quantity, setQuantity] = useState(1)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState("")
  const [emailRegistered, setEmailRegistered] = useState(false)
  const [completed, setCompleted] = useState(false)
  const [ticketIds, setTicketIds] = useState<string[]>([])
  const [userToken, setUserToken] = useState("")
  const [showBandInterestPrompt, setShowBandInterestPrompt] = useState(false)
  const [bandInterestTicketPurchaseId, setBandInterestTicketPurchaseId] =
    useState<number | null>(null)
  // The ticket's own ticket_id, not userToken - it's returned for every
  // guest purchase (including repeat ones, unlike userToken/single_use_token,
  // which is only issued once) and only proves control of this one ticket.
  const [bandInterestGuestToken, setBandInterestGuestToken] = useState("")
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [profileSaving, setProfileSaving] = useState(false)
  const [profileSaved, setProfileSaved] = useState(false)
  const [profileError, setProfileError] = useState("")
  const [saveCard, setSaveCard] = useState(true)
  const [emailIssue, setEmailIssue] = useState<EmailIssue | null>(null)
  const [emailConfirmed, setEmailConfirmed] = useState(false)

  const isSlidingScale = concert?.pricing_type === "sliding_scale"
  const effectiveTicketPrice = ticketPrice
  const ticketPriceMax = concert?.ticket_price_max as number | null ?? null
  const [chosenPrice, setChosenPrice] = useState<number | null>(null)
  const effectivePrice = isSlidingScale ? (chosenPrice ?? effectiveTicketPrice) : ticketPrice

  const maxTicketsAllowedToBuy =
    maxTickets > 0
      ? Math.min(MAX_GUEST_TICKETS, Math.max(0, maxTickets - ticketsSold))
      : MAX_GUEST_TICKETS

  const performPurchase = async () => {
    if (!stripe || !elements) return

    setIsSubmitting(true)
    setError("")

    try {
      const cardElement = elements.getElement(CardElement)
      if (!cardElement) {
        setError("Card form is not ready. Please try again.")
        return
      }

      const { paymentMethod, error: pmError } =
        await stripe.createPaymentMethod({
          type: "card",
          card: cardElement,
          billing_details: { email },
        })

      if (pmError) {
        setError(pmError.message || "Could not process card. Please try again.")
        return
      }

      const result = await guestCheckout({
        postId,
        email,
        paymentMethodId: paymentMethod!.id,
        quantity,
        chosenPrice: isSlidingScale ? (chosenPrice ?? effectiveTicketPrice) : undefined,
        redirectUrl: window.location.href,
        saveCard,
      })

      if (result.success && result.ticket_purchases?.length) {
        setTicketIds(result.ticket_purchases.map((tp) => tp.ticket_id))
        if (result.user_token) setUserToken(result.user_token)
        setBandInterestTicketPurchaseId(result.ticket_purchases[0].id)
        setBandInterestGuestToken(result.ticket_purchases[0].ticket_id)
        setShowBandInterestPrompt(true)
        setCompleted(true)
      } else if (result.error_code === "email_registered") {
        setEmailRegistered(true)
      } else {
        const message =
          result.error === "payment_failed"
            ? "Your card was declined. Please check your card details or try a different card."
            : result.error || "Payment failed. Please try again."
        setError(message)
      }
    } catch {
      setError("Something went wrong. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleEmailChange = (value: string) => {
    setEmail(value)
    setEmailConfirmed(false)
    setEmailIssue(null)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!emailConfirmed) {
      const issue = checkEmailForIssues(email)
      if (issue) {
        setEmailIssue(issue)
        return
      }
    }
    performPurchase()
  }

  const handleProfileSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!userToken || (!firstName && !lastName)) return
    setProfileSaving(true)
    setProfileError("")
    const result = await updateGuestProfile({
      token: userToken,
      firstName,
      lastName,
      redirectUrl: window.location.href,
    })
    setProfileSaving(false)
    if (result.success) {
      setProfileSaved(true)
    } else {
      setProfileError(
        result.errors?.join(", ") || "Could not save. Please try again.",
      )
    }
  }

  if (completed) {
    return (
      <div className="pt-2">
          <div className="text-green-600 font-bold text-lg mb-2">
            {ticketIds.length > 1 ? "Tickets Purchased!" : "Ticket Purchased!"} 🎉
          </div>
          <div className="text-sm text-gray-700 mb-3">
            Your {ticketIds.length > 1 ? `${ticketIds.length} tickets` : "ticket"}{" "}
            for <strong>{eventTitle}</strong>{" "}
            {ticketIds.length > 1 ? "have" : "has"} been confirmed. Check{" "}
            <strong>{email}</strong> for your ticket details.
          </div>
          {ticketIds.length > 0 && (
            <div className="p-3 bg-green-50 border border-green-200 rounded">
              <div className="text-sm text-green-800 mb-2">{email}</div>
              {ticketIds.map((id, index) => (
                <div key={id} className="font-mono text-sm text-green-800">
                  {ticketIds.length > 1 ? `${index + 1}. ` : ""}
                  {id}
                </div>
              ))}
            </div>
          )}

          <BandInterestPrompt
            isOpen={showBandInterestPrompt}
            postId={postId}
            ticketPurchaseId={bandInterestTicketPurchaseId}
            eventPerformances={concert?.event_performances}
            guestToken={bandInterestGuestToken}
          />

          {userToken && (
            <div className="mt-5 pt-4 border-t border-gray-200">
              {profileSaved ? (
                <div className="text-sm text-gray-700">
                  <div className="font-semibold text-green-700 mb-1">
                    Profile saved!
                  </div>
                  A login link has been sent to <strong>{email}</strong>. Use it
                  to access your bandNada account.
                </div>
              ) : (
                <>
                  <div className="font-semibold text-gray-800">
                    Finish bandNada Signup
                  </div>
                  <div className="text-sm text-gray-700 mb-3">
                    bandNada is the network to hear and connect with local
                    independent music. 100% Community Run, 100% Community Owned -{" "}
                    <a
                      href="/own-bandnada"
                      className="text-blue-600 hover:text-blue-800"
                    >
                      including you!
                    </a>
                  </div>
                  <form onSubmit={handleProfileSave}>
                    <div className="flex flex-col sm:flex-row gap-2 mb-4">
                      <input
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="First name"
                        className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-gray-500"
                      />
                      <input
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        placeholder="Last name"
                        className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-gray-500"
                      />
                    </div>
                    {profileError && (
                      <div className="text-red-500 text-xs mb-2">
                        {profileError}
                      </div>
                    )}
                    <button
                      type="submit"
                      disabled={profileSaving || (!firstName && !lastName)}
                      className="px-4 py-2 bg-gray-800 text-white rounded text-sm font-semibold focus:outline-none disabled:opacity-50"
                    >
                      {profileSaving ? "Saving..." : "Finish Signup"}
                    </button>
                  </form>
                </>
              )}
            </div>
          )}
        </div>
    )
  }

  if (emailRegistered) {
    return (
      <div className="pt-2">
        <div className="text-gray-800 mb-4">
          An account with <strong>{email}</strong> already exists. Please log in
          to purchase tickets.
        </div>
        <button
          className="px-4 py-2 bg-green-700 text-white rounded text-sm font-semibold focus:outline-none"
          onClick={() => {
            onClose()
            dispatch(openAuthDialog({ modalState: "auth" }))
          }}
        >
          Log In
        </button>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit}>
      {concert && (
        <TicketEventInfo
          concert={concert}
          ticketPrice={effectiveTicketPrice}
          ticketPriceMax={isSlidingScale ? ticketPriceMax : null}
          isSlidingScale={isSlidingScale}
          chosenPrice={chosenPrice}
          onChosenPriceChange={setChosenPrice}
          quantity={quantity}
          onQuantityChange={setQuantity}
          maxTicketsAllowedToBuy={maxTicketsAllowedToBuy}
        />
      )}

      <div className="mb-4">
        <div className="text-xs uppercase font-bold text-gray-700 mb-1">
          Email
        </div>
        <input
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => handleEmailChange(e.target.value)}
          required
          placeholder="your@email.com"
          className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-gray-500"
        />
        <div className="text-xs text-gray-500 mt-1">
          Your ticket confirmation will be sent here.
        </div>
        {emailIssue && (
          <div className="mt-2 p-3 bg-yellow-50 border border-yellow-300 rounded text-sm">
            <div className="text-gray-800 mb-2">{emailIssue.message}</div>
            <div className="flex gap-2">
              {emailIssue.suggestion ? (
                <>
                  <button
                    type="button"
                    className="px-3 py-1.5 bg-green-700 text-white rounded text-xs font-semibold focus:outline-none"
                    onClick={() => {
                      handleEmailChange(emailIssue.suggestion!)
                      setEmailConfirmed(true)
                    }}
                  >
                    Use {emailIssue.suggestion}
                  </button>
                  <button
                    type="button"
                    className="px-3 py-1.5 border border-gray-300 rounded text-xs font-semibold focus:outline-none"
                    onClick={() => {
                      setEmailConfirmed(true)
                      setEmailIssue(null)
                      performPurchase()
                    }}
                  >
                    No, use {email}
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="px-3 py-1.5 bg-green-700 text-white rounded text-xs font-semibold focus:outline-none"
                    onClick={() => {
                      setEmailConfirmed(true)
                      setEmailIssue(null)
                      performPurchase()
                    }}
                  >
                    Continue anyway
                  </button>
                  <button
                    type="button"
                    className="px-3 py-1.5 border border-gray-300 rounded text-xs font-semibold focus:outline-none"
                    onClick={() => setEmailIssue(null)}
                  >
                    Let me fix it
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <CardSection />

      <label className="flex items-center gap-2 text-sm text-gray-700 mt-3 mb-1 cursor-pointer">
        <input
          type="checkbox"
          checked={saveCard}
          onChange={(e) => setSaveCard(e.target.checked)}
        />
        Save card for future purchases
      </label>

      {error && <div className="text-red-500 text-sm mb-3">{error}</div>}

      <SubmitButton
        bg="bg-green-700"
        type="submit"
        active={!isSubmitting && !!stripe}
        className=""
      >
        {isSubmitting
          ? "Processing..."
          : `Buy ${formatPrice(effectivePrice * quantity)} Ticket${quantity > 1 ? "s" : ""}`}
      </SubmitButton>

      <div className="text-xs text-gray-500 mt-3">
        I authorize bandNada to charge my card for this ticket purchase.
      </div>

      <div className="text-sm text-gray-500 mt-4 pt-4 border-t border-gray-200">
        Already have a bandNada account?{" "}
        <button
          type="button"
          className="text-blue-600 hover:text-blue-800 focus:outline-none"
          onClick={() => {
            onClose()
            dispatch(openAuthDialog({ modalState: "auth" }))
          }}
        >
          Log in
        </button>
      </div>
    </form>
  )
}

const LoggedOutCheckoutModal: React.FC<LoggedOutCheckoutModalProps> = ({
  postId,
  bandName,
  eventTitle,
  ticketPrice,
  onClose,
  concert,
  maxTickets,
  ticketsSold,
}) => {
  const { spk } = useContext(UserContext)
  const stripePromiseRef = useRef<Promise<Stripe | null>>()
  if (!stripePromiseRef.current) {
    stripePromiseRef.current = loadStripe(spk)
  }

  return (
    <Dialog
      onDismiss={onClose}
      aria-label="Purchase ticket"
      className="relative"
    >
      <div className="flex justify-between items-start mb-4">
        <div></div>
        <button
          onClick={onClose}
          className="ml-4 text-gray-500 hover:text-gray-800 focus:outline-none"
          aria-label="Close"
        >
          <X size={20} />
        </button>
      </div>

      <Elements stripe={stripePromiseRef.current}>
        <GuestCheckoutForm
          postId={postId}
          bandName={bandName}
          eventTitle={eventTitle}
          ticketPrice={ticketPrice}
          onClose={onClose}
          concert={concert}
          maxTickets={maxTickets}
          ticketsSold={ticketsSold}
        />
      </Elements>
    </Dialog>
  )
}

export default LoggedOutCheckoutModal
